import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { coach } from '../../lib/api';
import { useTranslation } from 'react-i18next';
import { Plus, Calendar, Users, Clock, MapPin, Trash2, Edit2 } from 'lucide-react';

const CoachSessionsPage = () => {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [newSession, setNewSession] = useState({
    title: '',
    session_type: 'group_training',
    date: new Date().toISOString().split('T')[0],
    start_time: '10:00',
    end_time: '12:00',
    location: '',
    max_participants: 10,
    price: '',
    description: ''
  });

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    try {
      setLoading(true);
      const response = await coach.getSessions();
      setSessions(response.data);
    } catch (error) {
      console.error('Failed to load sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSession = async (e) => {
    e.preventDefault();
    try {
      await coach.createSession({
        ...newSession,
        date: new Date(newSession.date).toISOString(),
        price: newSession.price ? parseFloat(newSession.price) : null
      });
      setShowModal(false);
      setNewSession({
        title: '',
        session_type: 'group_training',
        date: new Date().toISOString().split('T')[0],
        start_time: '10:00',
        end_time: '12:00',
        location: '',
        max_participants: 10,
        price: '',
        description: ''
      });
      loadSessions();
    } catch (error) {
      console.error('Failed to create session:', error);
    }
  };

  const locale = i18n.language === 'ru' ? 'ru-RU' : i18n.language === 'fr' ? 'fr-FR' : 'en-GB';

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-8 mb-20 lg:mb-0">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-light tracking-tight text-primary">{t('coach.sessions')}</h1>
          <p className="text-text-muted mt-1">{t('coach.sessionsSubtitle')}</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-hover"
        >
          <Plus size={18} />
          {t('coach.createSession')}
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center text-primary">
              <Calendar size={24} />
            </div>
            <div>
              <p className="text-sm text-text-muted">{t('coach.totalSessions')}</p>
              <p className="text-2xl font-medium">{sessions.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center text-green-600">
              <Users size={24} />
            </div>
            <div>
              <p className="text-sm text-text-muted">{t('coach.enrolledCount')}</p>
              <p className="text-2xl font-medium">
                {sessions.reduce((sum, s) => sum + (s.enrolled?.length || 0), 0)}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600">
              <Clock size={24} />
            </div>
            <div>
              <p className="text-sm text-text-muted">{t('coach.upcomingSessions')}</p>
              <p className="text-2xl font-medium">
                {sessions.filter(s => new Date(s.date) > new Date()).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Sessions List */}
      <div className="space-y-4">
        {sessions.map((session) => {
          const isPast = new Date(session.date) < new Date();
          const enrolledCount = session.enrolled?.length || 0;
          const isFull = enrolledCount >= session.max_participants;

          return (
            <div key={session.id} className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-medium">{session.title}</h3>
                    <span className="px-2 py-1 bg-primary/10 text-primary text-xs rounded">
                      {t(`coach.sessionTypes.${session.session_type}`, { defaultValue: session.session_type })}
                    </span>
                    {isPast && (
                      <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded">
                        {t('coach.completed')}
                      </span>
                    )}
                    {isFull && !isPast && (
                      <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded">
                        {t('coach.full')}
                      </span>
                    )}
                  </div>

                  {session.description && (
                    <p className="text-text-muted mb-4">{session.description}</p>
                  )}

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="flex items-center gap-2">
                      <Calendar size={18} className="text-text-muted" />
                      <div>
                        <p className="text-xs text-text-muted">{t('common.date')}</p>
                        <p className="font-medium">
                          {new Date(session.date).toLocaleDateString(locale)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock size={18} className="text-text-muted" />
                      <div>
                        <p className="text-xs text-text-muted">{t('coach.time')}</p>
                        <p className="font-medium">
                          {session.start_time} - {session.end_time}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin size={18} className="text-text-muted" />
                      <div>
                        <p className="text-xs text-text-muted">{t('coach.location')}</p>
                        <p className="font-medium">{session.location || t('coach.noLocation')}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Users size={18} className="text-text-muted" />
                      <div>
                        <p className="text-xs text-text-muted">{t('coach.participants')}</p>
                        <p className="font-medium">
                          {enrolledCount} / {session.max_participants}
                        </p>
                      </div>
                    </div>
                  </div>

                  {session.price && (
                    <div className="mt-4 pt-4 border-t border-slate-100">
                      <p className="text-sm text-text-muted">{t('coach.price')}: <span className="font-medium">{session.price} ₽</span></p>
                    </div>
                  )}

                  {enrolledCount > 0 && (
                    <div className="mt-4 pt-4 border-t border-slate-100">
                      <p className="text-sm text-text-muted mb-2">{t('coach.enrolled')}</p>
                      <div className="flex flex-wrap gap-2">
                        {session.enrolled.map((userId, index) => (
                          <span key={`${userId}-${index}`} className="px-2 py-1 bg-slate-100 rounded text-sm">
                            {t('coach.participant')} {index + 1}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled
                    title={t('admin.inDevelopment')}
                    className="p-2 text-text-muted rounded opacity-50 cursor-not-allowed"
                  >
                    <Edit2 size={18} />
                  </button>
                  <button
                    type="button"
                    disabled
                    title={t('admin.inDevelopment')}
                    className="p-2 text-text-muted rounded opacity-50 cursor-not-allowed"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
        {sessions.length === 0 && (
          <div className="text-center py-12">
            <Calendar className="mx-auto text-slate-300 mb-4" size={48} />
            <p className="text-text-muted">{t('coach.noSessions')}</p>
          </div>
        )}
      </div>

      {/* Create Session Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium mb-4">{t('coach.newSession')}</h3>
            <form onSubmit={handleCreateSession} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">{t('coach.sessionTitle')}</label>
                <input
                  type="text"
                  value={newSession.title}
                  onChange={(e) => setNewSession({...newSession, title: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-md"
                  placeholder={t('coach.sessionTitlePlaceholder')}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">{t('coach.sessionType')}</label>
                <select
                  value={newSession.session_type}
                  onChange={(e) => setNewSession({...newSession, session_type: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-md"
                >
                  <option value="group_training">{t('coach.sessionTypes.group_training')}</option>
                  <option value="clinic">{t('coach.sessionTypes.clinic')}</option>
                  <option value="workshop">{t('coach.sessionTypes.workshop')}</option>
                  <option value="individual">{t('coach.sessionTypes.individual')}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">{t('coach.sessionDate')}</label>
                <input
                  type="date"
                  value={newSession.date}
                  onChange={(e) => setNewSession({...newSession, date: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-md"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">{t('coach.sessionStart')}</label>
                  <input
                    type="time"
                    value={newSession.start_time}
                    onChange={(e) => setNewSession({...newSession, start_time: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-md"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">{t('coach.sessionEnd')}</label>
                  <input
                    type="time"
                    value={newSession.end_time}
                    onChange={(e) => setNewSession({...newSession, end_time: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-md"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">{t('coach.venue')}</label>
                <input
                  type="text"
                  value={newSession.location}
                  onChange={(e) => setNewSession({...newSession, location: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-md"
                  placeholder={t('coach.venuePlaceholder')}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">{t('coach.maxParticipants')}</label>
                <input
                  type="number"
                  value={newSession.max_participants}
                  onChange={(e) => setNewSession({...newSession, max_participants: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-md"
                  min="1"
                  max="50"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">{t('coach.priceCurrency')}</label>
                <input
                  type="number"
                  value={newSession.price}
                  onChange={(e) => setNewSession({...newSession, price: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-md"
                  placeholder={t('coach.freeHint')}
                  min="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">{t('coach.description')}</label>
                <textarea
                  value={newSession.description}
                  onChange={(e) => setNewSession({...newSession, description: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-md"
                  rows={3}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-2 border border-slate-200 rounded-md hover:bg-slate-50"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-hover"
                >
                  {t('common.create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CoachSessionsPage;
